import React, { useState } from "react";
import { ReactComponent as UpButton } from 'src/assets/upButton.svg';
import { ReactComponent as DownButton } from 'src/assets/downButton.svg';
import { useIsMobile } from "../../hooks/useIsMobile";
import { useNavigate } from "react-router-dom";

interface AuthorData {
    id?: number;
    name?: string;
    desc?: string;
    email: string;
    profile?: string;
}

const BookAuthorInfo = ({ id, name, desc, email, profile }: AuthorData) => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const profileImg = profile
        ? `data:image/jpeg;base64,${profile}`
        : `/img/profile_sample.jpg`;

    const isLongDescription = (desc ?? "").length > 200;

    const moveToAuthorDetail = (authorName: string, authorEmail: string, authorId?: number) => {
        if (authorId) {
            navigate(`/author/authordetails/${authorId}`, {
                state: { authorEmail },
            });
        } else {
            navigate(`/author/authordetails/${authorName.replace(/\s+/g, '')}`, {
                state: { authorEmail },
            });
        }
    };

    /* ── Mobile ── */
    if (isMobile) {
        return (
            <div className="flex flex-col gap-4 pt-5">
                {/* 프로필 행 */}
                <div
                    className="flex flex-row gap-3 items-center cursor-pointer"
                    onClick={() => moveToAuthorDetail(name || '', email, id)}
                >
                    {/* 그라디언트 테두리 아바타 */}
                    <div
                        className="w-[48px] h-[48px] rounded-full flex-shrink-0 p-[2px]"
                        style={{
                            background: 'linear-gradient(135deg, #19cf7a 0%, #00d695 100%)',
                        }}
                    >
                        <img
                            src={profileImg}
                            alt="author"
                            className="w-full h-full rounded-full object-cover bg-white"
                        />
                    </div>
                    <div className="flex items-baseline gap-1">
                        <span className="font-bold text-[24px] text-black">{name}</span>
                        <span className="text-[16px] text-[rgba(0,0,0,0.6)]">작가</span>
                    </div>
                </div>

                {/* 소개 */}
                <p className="font-namsan text-[15px] text-[#565659] leading-[28px]">
                    {isExpanded
                        ? desc
                        : `${(desc ?? "").slice(0, 200)}${(desc ?? "").length > 200 ? "..." : ""}`}
                </p>

                {isLongDescription && (
                    <div className="flex justify-end">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="flex items-center gap-1.5 text-[#565659] font-namsan text-[16px]"
                        >
                            {isExpanded ? (
                                <><UpButton /><span>접기</span></>
                            ) : (
                                <><DownButton /><span>더보기</span></>
                            )}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    /* ── Desktop ── */
    return (
        <div className="flex flex-col gap-6">
            {/* 프로필 행 */}
            <div
                className="flex flex-row items-center gap-5 cursor-pointer"
                onClick={() => moveToAuthorDetail(name || '', email, id)}
            >
                {/* 그라디언트 테두리 아바타 */}
                <div
                    className="w-[80px] h-[80px] rounded-full flex-shrink-0 p-[3px]"
                    style={{
                        background: 'linear-gradient(135deg, #19cf7a 0%, #00d695 100%)',
                    }}
                >
                    <img
                        src={profileImg}
                        alt="author"
                        className="w-full h-full rounded-full object-cover bg-white"
                    />
                </div>

                <div className="flex items-baseline gap-2">
                    <span className="font-bold text-[36px] text-black">{name}</span>
                    <span className="font-semibold text-[22px] text-black">작가</span>
                </div>
            </div>

            {/* 소개 텍스트 */}
            <p className="font-namsan text-[20px] text-[#565659] leading-[1.8]">
                {isExpanded
                    ? desc
                    : `${(desc ?? "").slice(0, 200)}${(desc ?? "").length > 200 ? "..." : ""}`}
            </p>

            {isLongDescription && (
                <div className="flex justify-end">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center gap-2 text-[#565659] font-namsan text-[18px]"
                    >
                        {isExpanded ? (
                            <><UpButton /><span>접기</span></>
                        ) : (
                            <><DownButton /><span>더보기</span></>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default BookAuthorInfo;
